import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Property {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  short_description: string | null;
  property_type: 'house' | 'apartment' | 'condo' | 'villa' | 'land' | 'commercial';
  transaction_type: 'sale' | 'rent' | 'both';
  price: number | null;
  rental_price: number | null;
  currency: string;
  bedrooms: number | null;
  bathrooms: number | null;
  area_sqm: number | null;
  area_sqft: number | null;
  lot_size_sqm: number | null;
  lot_size_sqft: number | null;
  year_built: number | null;
  location_province: string | null;
  location_city: string | null;
  location_zone: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  main_image_url: string | null;
  gallery_images_urls: string[] | null;
  video_url: string | null;
  virtual_tour_url: string | null;
  features: string[] | null;
  amenities: string[] | null;
  is_featured: boolean;
  is_active: boolean;
  status: 'available' | 'reserved' | 'sold' | 'rented';
  views_count: number;
  category_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PropertyFilters {
  property_type?: string[];
  transaction_type?: string;
  min_price?: number;
  max_price?: number;
  location_province?: string[];
  bedrooms?: number;
  bathrooms?: number;
  status?: string[];
  is_featured?: boolean;
}

export const useProperties = (filters?: PropertyFilters) => {
  return useQuery({
    queryKey: ['properties', filters],
    queryFn: async () => {
      let query = supabase
        .from('properties')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (filters?.property_type && filters.property_type.length > 0) {
        query = query.in('property_type', filters.property_type as any);
      }

      if (filters?.transaction_type) {
        query = query.eq('transaction_type', filters.transaction_type as any);
      }

      if (filters?.min_price) {
        query = query.gte('price', filters.min_price);
      }

      if (filters?.max_price) {
        query = query.lte('price', filters.max_price);
      }

      if (filters?.location_province && filters.location_province.length > 0) {
        query = query.in('location_province', filters.location_province);
      }

      if (filters?.bedrooms) {
        query = query.gte('bedrooms', filters.bedrooms);
      }

      if (filters?.bathrooms) {
        query = query.gte('bathrooms', filters.bathrooms);
      }

      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status as any);
      }

      if (filters?.is_featured) {
        query = query.eq('is_featured', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Property[];
    },
  });
};

export const useProperty = (slug: string) => {
  return useQuery({
    queryKey: ['property', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data as Property;
    },
    enabled: !!slug,
  });
};

export const useAdminProperties = () => {
  return useQuery({
    queryKey: ['admin-properties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Property[];
    },
  });
};

export const useCreateProperty = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (property: Omit<Property, 'id' | 'created_at' | 'updated_at' | 'views_count'>) => {
      const { data, error } = await supabase
        .from('properties')
        .insert([property])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['admin-properties'] });
      toast({
        title: "Success",
        description: "Property created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateProperty = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Property> & { id: string }) => {
      const { data, error } = await supabase
        .from('properties')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['admin-properties'] });
      toast({
        title: "Success",
        description: "Property updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteProperty = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['admin-properties'] });
      toast({
        title: "Success",
        description: "Property deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useIncrementPropertyViews = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: property } = await supabase
        .from('properties')
        .select('views_count')
        .eq('id', id)
        .single();

      if (property) {
        await supabase
          .from('properties')
          .update({ views_count: (property.views_count || 0) + 1 })
          .eq('id', id);
      }
    },
  });
};
